import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../routes/Routes';
import { MapTemplate } from '@templates';
import { LatLngTuple } from 'leaflet';
//import 'leaflet/dist/leaflet.css';
import {
  AssetTypeConfig,
  ErrorMessages,
  ServiceLocationDetailsObjectType,
} from '@types';
import {
  addressResponseType,
  errorFormatter,
  getNearestAsset,
  issueLocationValidation,
} from '@utils';
import { useBuskingStore } from '../../../../hooks/useBuskingStore';
import { ZodError } from 'zod';

const emptyLocation: addressResponseType = {
  address_line_1: '',
  address_line_2: '',
  town: '',
  postcode: '',
  country: '',
  UPRN: '',
  USRN: '',
  latitude: '',
  longitude: '',
  easting: '',
  northing: '',
  county: '',
  full_address: '',
  asset: undefined,
};

export const IssueLocation: FunctionComponent = () => {
  const navigate = useNavigate();
  const { data, get, update, resetSelective } = useBuskingStore();
  const issueLocationData = get('issueLocationData');

  const initialAddress: addressResponseType | ServiceLocationDetailsObjectType =
    get('isPhotoLocationStored') && get('serviceLocationDetailsFromFile')
      ? data.serviceLocationDetailsFromFile
      : issueLocationData?.address_line_1
        ? issueLocationData
        : ({} as addressResponseType);

  const getLatLongTuple = (
    address: addressResponseType,
  ): LatLngTuple | undefined => {
    if (address?.latitude && address?.longitude) {
      const lat = parseFloat(address?.latitude);
      const long = parseFloat(address?.longitude);
      return [lat, long];
    }
    return undefined;
  };

  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const [errorPaths, setErrorPaths] = useState<string>('');
  const [serviceLocationDetails, setServiceLocationDetails] =
    useState<addressResponseType>(initialAddress as addressResponseType);

  const [defaultLocation, setDefaultLocation] = useState<
    LatLngTuple | undefined
  >(getLatLongTuple(initialAddress as addressResponseType));
  const isPhotoLocationStored = get('isPhotoLocationStored');
  const pageTitle = isPhotoLocationStored
    ? `Check if the location is correct`
    : `Location of the issue`;

  const subText = isPhotoLocationStored ? (
    <div className="text-bodyLg flex flex-col gap-4">
      <p>We used your photo to find where the issue is located.</p>
      <p>You can change where the issue is located using the map.</p>
    </div>
  ) : (
    'Please select a location on the map'
  );

  const handleOnPreviousClick = () => {
    navigate(RoutesList.PHOTO_UPLOAD);
  };

  const handleOnNextClick = () => {
    try {
      issueLocationValidation.parse({
        locationIssue: issueLocationData.UPRN ?? '',
      });
      if (
        !serviceLocationDetails ||
        !serviceLocationDetails.latitude ||
        !serviceLocationDetails.longitude
      ) {
        return;
      } else {
        getNearestAsset(
          Number(serviceLocationDetails.latitude),
          Number(serviceLocationDetails.longitude),
          'StreetEntertainmentPitches',
        );
      }
      update('issueLocationData', serviceLocationDetails);
      setErrorMessages([]);

      navigate(RoutesList.EMAIL_CONFIRMATION_REPORT);
    } catch (error) {
      const { errorPaths, errorMessages } = errorFormatter(error as ZodError);
      setErrorMessages(errorMessages);
      setErrorPaths(errorPaths);
    }
  };

  const handleAddressChange = (address: addressResponseType) => {
    setServiceLocationDetails(address);
    setDefaultLocation([Number(address.latitude), Number(address.longitude)]);
    const updatedServiceLocation = address.address_line_1
      ? address
      : emptyLocation;

    update(`issueLocationData`, updatedServiceLocation as unknown as any);
    setErrorMessages([]);
  };

  const buttonPrevious = {
    label: 'Previous',
    onClick: handleOnPreviousClick,
  };

  const buttonNext = {
    label: 'Next',
    onClick: handleOnNextClick,
  };

  useEffect(() => {
    if (get('issueLocationData')) {
      const loc: LatLngTuple | undefined = getLatLongTuple(
        data.issueLocationData,
      );
      setDefaultLocation(loc);
    }
  }, [get('issueLocationData')]);

  const StreetEntertainmentPitchesAsset: AssetTypeConfig = {
    asset: 'StreetEntertainmentPitches',
    icon: {
      shape: 'square',
    },
  };
  const isBufferNeeded = get('issueTypes').some(
    (type) => type.name === 'Noise',
  );

  return (
    <MapTemplate
      id={errorPaths}
      labelText={pageTitle}
      assetType={[StreetEntertainmentPitchesAsset]}
      displayMapAssets={true}
      subText={subText}
      errorPath={errorPaths}
      errorMessages={errorMessages}
      onChangeAddress={handleAddressChange}
      enableBuffer={isBufferNeeded ? true : false}
      defaultLocation={defaultLocation}
      selectedAddress={serviceLocationDetails}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
    />
  );
};
